import { useState, useEffect } from "react";
import { postDataAPI, getDataAPI } from "../../../utils/fetchData";
import CustomCalendar from "../../../components/Calendar/CustomCalendar";
import Modal from "../../../components/Modal";
import { useModal } from "../../../hooks/useModal";
import { markReleaseAdded } from "../../../utils/onboardingStateMachine";
import AccessibilityWidget from "../../../components/AccessibilityWidget";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  position: relative;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.3)' : 'white'};
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  height: 18px;
  line-height: 18px;
  position: relative;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  font-size: 12px;
  width: 100%;
  padding: 0;
  color: white;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    caret-color: white;
  }
`;

const Select = styled.select`
  background: transparent;
  border: none;
  font-size: 12px;
  width: 100%;
  padding: 0;
  color: white;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  option {
    background: black;
    color: white;
  }
`;

const ReleaseSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 8px;
`;

const ReleaseButton = styled.button`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
  background: ${props => props.active ? '#17A2B8' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  border: none;
  cursor: pointer;
`;

const FieldContainer = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  color: white;
  margin-bottom: 8px;
  font-weight: 300;
`;

const ArtistInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

const ArtistInput = styled(Input)`
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 4px 8px;
`;

const Step11b = ({ setStep, setUser, user }) => {
    const [releases, setReleases] = useState([
        {
            song_title: '',
            release_date: '',
            release_status: 'Upcoming',
            primary_artists: ['']
        }
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentReleaseIndex, setCurrentReleaseIndex] = useState(0);
    const { modalConfig, showModal, closeModal } = useModal();

    // Dropdown options
    const releaseStatusOptions = ['Upcoming', 'Past', 'Demo'];

    useEffect(() => {
        console.log('🎵 Step11b (Add Releases) - Component mounted and rendering');
        // Prevent scroll
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.touchAction = 'none';
        return () => {
            document.body.style.overflow = 'unset';
            document.documentElement.style.overflow = 'unset';
            document.body.style.position = 'static';
            document.body.style.width = 'auto';
            document.body.style.height = 'auto';
            document.body.style.touchAction = 'auto';
        };
    }, []);

    const addRelease = () => {
        setReleases(prev => [...prev, {
            song_title: '',
            release_date: '',
            release_status: 'Upcoming',
            primary_artists: ['']
        }]);
        setCurrentReleaseIndex(releases.length);
    };

    const removeRelease = (index) => {
        if (releases.length > 1) {
            setReleases(prev => prev.filter((_, i) => i !== index));
            if (currentReleaseIndex >= releases.length - 1) {
                setCurrentReleaseIndex(Math.max(0, currentReleaseIndex - 1));
            }
        }
    };

    const updateRelease = (index, key, value) => {
        setReleases(prev => prev.map((release, i) => 
            i === index ? { ...release, [key]: value } : release
        ));
    };

    const addPrimaryArtist = (releaseIndex) => {
        setReleases(prev => prev.map((release, i) => {
            if (i === releaseIndex) {
                if (release.primary_artists.length < 6) {
                    return {
                        ...release,
                        primary_artists: [...release.primary_artists, '']
                    };
                }
            }
            return release;
        }));
    };

    const removePrimaryArtist = (releaseIndex, artistIndex) => {
        setReleases(prev => prev.map((release, i) => {
            if (i === releaseIndex) {
                if (release.primary_artists.length > 1) {
                    return {
                        ...release,
                        primary_artists: release.primary_artists.filter((_, ai) => ai !== artistIndex)
                    };
                }
            }
            return release;
        }));
    };

    const updatePrimaryArtist = (releaseIndex, artistIndex, value) => {
        setReleases(prev => prev.map((release, i) => {
            if (i === releaseIndex) {
                return {
                    ...release,
                    primary_artists: release.primary_artists.map((artist, ai) => 
                        ai === artistIndex ? value : artist
                    )
                };
            }
            return release;
        }));
    };

    const saveReleases = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('dizeeToken');
            
            // Filter out empty releases and validate
            const validReleases = releases.filter(release => {
                const hasTitle = release.song_title && release.song_title.trim() !== '';
                const hasDate = release.release_date && release.release_date !== '';
                const hasArtist = release.primary_artists.some(artist => artist && artist.trim() !== '');
                return hasTitle && hasDate && hasArtist;
            });

            if (validReleases.length === 0) {
                showModal('Validation Error', 'Please fill in at least one release with song title, release date, and at least one primary artist.', 'warning');
                setIsLoading(false);
                return;
            }

            // Prepare data for API
            const releasesToSave = validReleases.map(release => ({
                song_title: release.song_title?.trim() || '',
                release_date: release.release_date || null,
                release_status: release.release_status || 'Upcoming',
                primary_artist: Array.isArray(release.primary_artists) 
                    ? release.primary_artists.filter(artist => artist && artist.trim() !== '').join(', ')
                    : (release.primary_artists || ''),
            }));

            // Check if user is authenticated (has token)
            if (!token) {
                // User not authenticated yet - store releases temporarily
                console.log('📝 User not authenticated yet, storing releases temporarily...');
                localStorage.setItem('pendingCatalogReleases', JSON.stringify(releasesToSave));
                
                // Navigate immediately without showing modal
                console.log('✅ Step11b: Releases saved (pending), going to step 12');
                setStep(12);
                setIsLoading(false);
                return;
            }

            // User is authenticated - save directly
            const parsedToken = JSON.parse(token);
            
            // Get existing catalog to check for duplicates
            let existingCatalog = [];
            try {
                const catalogResponse = await getDataAPI('user/getCatalog', parsedToken);
                if (catalogResponse?.data?.status === 200 && catalogResponse?.data?.data) {
                    existingCatalog = Array.isArray(catalogResponse.data.data) 
                        ? catalogResponse.data.data 
                        : catalogResponse.data.data.data;
                }
            } catch (catalogError) {
                console.warn('Could not fetch existing catalog:', catalogError);
            }

            // Filter out duplicates
            const normalize = (str) => str ? str.toLowerCase().trim() : '';
            const newReleases = releasesToSave.filter(newRelease => {
                return !existingCatalog.some(existingTrack => {
                    const newSongName = normalize(newRelease.song_title);
                    const newArtistName = normalize(newRelease.primary_artist);
                    const existingSongName = normalize(existingTrack.song_title || existingTrack.song_name || existingTrack.title);
                    const existingArtistName = normalize(existingTrack.primary_artist || existingTrack.artist_name || existingTrack.artist);
                    return newSongName && newArtistName && 
                           newSongName === existingSongName && 
                           newArtistName === existingArtistName;
                });
            });

            if (newReleases.length === 0) {
                showModal('Duplicate Releases', 'All releases you entered already exist in your catalog.', 'warning');
                setIsLoading(false);
                return;
            }

            // Save releases
            const response = await postDataAPI('user/saveCatalog', {
                data: newReleases
            }, parsedToken);

            if (response.data.status === 200) {
                // Mark release as added for onboarding
                markReleaseAdded();
                
                // Trigger reminder system update
                localStorage.setItem('catalog_updated', Date.now().toString());
                window.dispatchEvent(new CustomEvent('localStorageUpdate', {
                    detail: {
                        key: 'catalog_updated',
                        newValue: Date.now().toString()
                    }
                }));

                showModal('Success', `Successfully added ${newReleases.length} release(s) to your catalog!`, 'success');
                
                setTimeout(() => {
                    console.log('✅ Step11b: Releases saved successfully, going to step 12');
                    setStep(12);
                }, 1500);
            } else {
                const errorMessage = response.data.message || 'Error saving releases. Please try again.';
                showModal('Save Error', errorMessage, 'error');
            }
        } catch (error) {
            console.error('Error saving releases:', error);
            showModal('Error', 'An error occurred while saving releases. Please try again.', 'error');
        }
        setIsLoading(false);
    };

    const handleSkip = () => {
        console.log('⏭️ Step11b: User skipped adding releases, going to step 12');
        setStep(12);
    };

    const currentRelease = releases[currentReleaseIndex] || releases[0];

    return (
        <Container>
            <FormSection>
                <TypeWriter text={"Add your upcoming releases"} speed={45} />
                
                <div className="w-[200px]">
                    {/* Release Selector */}
                    {releases.length > 1 && (
                        <ReleaseSelector style={{ marginBottom: '20px' }}>
                            {releases.map((_, index) => (
                                <ReleaseButton
                                    key={index}
                                    active={currentReleaseIndex === index}
                                    onClick={() => setCurrentReleaseIndex(index)}
                                >
                                    Release {index + 1}
                                </ReleaseButton>
                            ))}
                            <ReleaseButton onClick={addRelease}>
                                + Add
                            </ReleaseButton>
                        </ReleaseSelector>
                    )}

                    {/* Song Title */}
                    <FieldContainer>
                        <Label>Song Title <span style={{ color: '#17A2B8' }}>*</span></Label>
                        <Input
                            type="text"
                            value={currentRelease.song_title || ''}
                            onChange={(e) => updateRelease(currentReleaseIndex, 'song_title', e.target.value)}
                            placeholder="Enter song title"
                        />
                    </FieldContainer>

                    {/* Release Status */}
                    <FieldContainer>
                        <Label>Release Status <span style={{ color: '#17A2B8' }}>*</span></Label>
                        <Select
                            value={currentRelease.release_status || 'Upcoming'}
                            onChange={(e) => updateRelease(currentReleaseIndex, 'release_status', e.target.value)}
                        >
                            {releaseStatusOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                    </FieldContainer>

                    {/* Release Date */}
                    <FieldContainer>
                        <CustomCalendar
                            label="Release Date *"
                            value={currentRelease.release_date || ''}
                            onChange={(date) => updateRelease(currentReleaseIndex, 'release_date', date)}
                            placeholder="Select release date"
                            showTime={false}
                            required={true}
                            theme="catalog"
                        />
                    </FieldContainer>

                    {/* Primary Artists */}
                    <FieldContainer>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Label style={{ marginBottom: 0 }}>Primary Artists <span style={{ color: '#17A2B8' }}>*</span></Label>
                            {currentRelease.primary_artists.length < 6 && (
                                <ActionButton
                                    onClick={() => addPrimaryArtist(currentReleaseIndex)}
                                    style={{ fontSize: '10px', color: '#17A2B8' }}
                                >
                                    + Add
                                </ActionButton>
                            )}
                        </div>
                        {currentRelease.primary_artists.map((artist, index) => (
                            <ArtistInputContainer key={index}>
                                <ArtistInput
                                    type="text"
                                    value={artist}
                                    onChange={(e) => updatePrimaryArtist(currentReleaseIndex, index, e.target.value)}
                                    placeholder={`Primary artist ${index + 1}`}
                                />
                                {currentRelease.primary_artists.length > 1 && (
                                    <ActionButton
                                        onClick={() => removePrimaryArtist(currentReleaseIndex, index)}
                                        style={{ fontSize: '10px', color: '#FF9999', padding: '4px' }}
                                    >
                                        ×
                                    </ActionButton>
                                )}
                            </ArtistInputContainer>
                        ))}
                    </FieldContainer>

                    {/* Remove Release Button (if multiple) */}
                    {releases.length > 1 && (
                        <FieldContainer>
                            <ActionButton
                                onClick={() => removeRelease(currentReleaseIndex)}
                                style={{ fontSize: '10px', color: '#FF9999' }}
                            >
                                Remove This Release
                            </ActionButton>
                        </FieldContainer>
                    )}

                    {/* Add Another Release Button */}
                    {releases.length === 1 && (
                        <FieldContainer>
                            <ActionButton
                                onClick={addRelease}
                                style={{ fontSize: '10px', color: 'white' }}
                            >
                                + Add Another Release
                            </ActionButton>
                        </FieldContainer>
                    )}

                    <ButtonContainer>
                        <ActionButton
                            disabled={isLoading}
                            onClick={saveReleases}
                            className="mr-[43px]"
                        >
                            {isLoading ? 'Saving...' : 'Save'}
                        </ActionButton>
                        <ActionButton onClick={handleSkip}>
                            Skip
                        </ActionButton>
                        <ActionButton
                            onClick={() => {
                                console.log('⏪ Step11b: Going back to step 22 (What should we call you)');
                                setStep(22);
                            }}
                            style={{ marginLeft: '43px' }}
                        >
                            Go back
                        </ActionButton>
                    </ButtonContainer>
                </div>
            </FormSection>

            <Footer>
                <LogoImage src={dizeeLogo} alt="DIZEE" />
                <FooterText>
                    Add your upcoming releases. You can add multiple releases and skip this step if you prefer.
                </FooterText>
            </Footer>

            <Modal
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                showCancel={modalConfig.showCancel}
                confirmText={modalConfig.confirmText}
                cancelText={modalConfig.cancelText}
            />
            <AccessibilityWidget />
        </Container>
    );
};

export default Step11b;
